import { ApiError } from '../api/client';

/**
 * Same 403/400/fallback idiom `menu-form-schema.ts`'s `genericMessage`
 * already applies. Shared here so `mapPriceError`/`mapDocumentError` (both
 * new in this PR) do not each re-derive the same `ApiError` narrowing and
 * status fallback (`tasks.md`'s own REFACTOR requirement for this PR: no
 * duplicated 422/409 mapping between the new tabs).
 */
function genericApiErrorMessage(error: ApiError): string {
  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 400) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}

/** `contract-prices/spec.md` → codes from `ContractsExceptionFilter` (`design.md` §8). */
export function mapPriceError(error: unknown): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 422 && code === 'MENU_NOT_ELIGIBLE') {
    return 'Ese menú no está disponible para asignarle un precio (inactivo o archivado).';
  }

  if (error.status === 422 && code === 'PRICE_PERIOD_OVERLAP') {
    return 'Ya existe un precio vigente para ese menú en ese período.';
  }

  if (
    error.status === 422 &&
    (code === 'VAT_RATE_NOT_SUPPORTED' || code === 'VAT_RATE_INACTIVE')
  ) {
    return 'Esa alícuota ya no está disponible. Elegí otra.';
  }

  if (error.status === 409 && code === 'CONTRACT_VERSION_LOCKED') {
    return 'Esta versión del contrato está cerrada y no admite cambios.';
  }

  return genericApiErrorMessage(error);
}

/** Activate / pause / terminate: the action is the verb in the fallback ("activar", "pausar", "dar de baja"). */
export function mapTransitionError(error: unknown, action: string): string {
  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 422 && code === 'CONTRACT_NO_ACTIVE_PRICE') {
    return 'Cargá al menos un precio vigente antes de activar el contrato.';
  }

  if (error.status === 409 && code === 'CONTRACT_UNIT_PERIOD_OVERLAP') {
    return 'Ya hay un contrato vigente de esta empresa en este punto de venta en esas fechas.';
  }

  if (error.status === 409 && code === 'CONTRACT_INVALID_TRANSITION') {
    return `El contrato cambió de estado y ya no se puede ${action}. Actualizá la página.`;
  }

  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  return `No pudimos ${action} el contrato.`;
}

/** `contract-documents/spec.md` → codes from `ContractsExceptionFilter` (`design.md` §8). */
export function mapDocumentError(error: unknown): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 422 && code === 'DOCUMENT_INVALID_TYPE') {
    return 'El archivo debe ser un PDF.';
  }

  if (error.status === 422 && code === 'DOCUMENT_TOO_LARGE') {
    return 'El archivo supera el tamaño máximo permitido (5 MB).';
  }

  if (error.status === 409 && code === 'CONTRACT_VERSION_LOCKED') {
    return 'Esta versión del contrato está cerrada y no admite cambios.';
  }

  return genericApiErrorMessage(error);
}
